import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

const empty = { parent_type: "car", parent_slug: "", question: "", answer: "", order: 0 };

export default function FAQsAdmin() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ parent_type: "", parent_slug: "" });
  const [creating, setCreating] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (filter.parent_type) params.set("parent_type", filter.parent_type);
    if (filter.parent_slug) params.set("parent_slug", filter.parent_slug);
    api.get(`/admin/faqs?${params.toString()}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [filter]);

  const save = async (faq, id) => {
    try {
      if (id) {
        await api.put(`/admin/faqs/${id}`, faq);
      } else {
        await api.post("/admin/faqs", faq);
      }
      toast.success("Saved");
      setCreating(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    await api.delete(`/admin/faqs/${id}`);
    load();
  };

  return (
    <div data-testid="admin-faqs">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tighter">FAQs</h1>
          <p className="text-sm text-muted-foreground mt-1">Attach frequently-asked questions to cars, articles or reviews.</p>
        </div>
        <button data-testid="faq-new" onClick={() => setCreating({ ...empty })} className="btn-primary h-10 px-4 text-sm inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> New FAQ
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <select data-testid="faq-filter-type" value={filter.parent_type} onChange={(e) => setFilter({ ...filter, parent_type: e.target.value })}
          className="h-10 rounded-full border border-[#E5E7EB] bg-white px-3 text-sm">
          <option value="">All types</option>
          <option value="car">Car</option>
          <option value="article">Article</option>
          <option value="review">Review</option>
          <option value="global">Global</option>
        </select>
        <input data-testid="faq-filter-slug" value={filter.parent_slug} onChange={(e) => setFilter({ ...filter, parent_slug: e.target.value })}
          placeholder="Filter by parent slug"
          className="h-10 rounded-full border border-[#E5E7EB] bg-white px-4 text-sm w-56"/>
      </div>

      {creating && <FAQForm faq={creating} onCancel={() => setCreating(null)} onSave={(f) => save(f)}/>}

      <div className="mt-6 space-y-3">
        {rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground text-sm">No FAQs yet.</div>
        ) : rows.map((f) => (
          <FAQRow key={f.id} faq={f} onSave={(u) => save(u, f.id)} onDelete={() => del(f.id)}/>
        ))}
      </div>
    </div>
  );
}

function FAQForm({ faq, onCancel, onSave }) {
  const [f, setF] = useState(faq);
  return (
    <div className="mt-6 rounded-3xl border border-[#E5E7EB] bg-white p-5" data-testid="faq-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <select data-testid="faq-parent-type" value={f.parent_type} onChange={(e) => setF({ ...f, parent_type: e.target.value })} className={inp}>
          <option value="car">Car</option><option value="article">Article</option><option value="review">Review</option><option value="global">Global</option>
        </select>
        <input data-testid="faq-parent-slug" placeholder="parent slug (or 'global')" value={f.parent_slug} onChange={(e) => setF({ ...f, parent_slug: e.target.value })} className={inp}/>
        <input data-testid="faq-order" type="number" placeholder="Order" value={f.order} onChange={(e) => setF({ ...f, order: parseInt(e.target.value || "0", 10) })} className={inp}/>
      </div>
      <input data-testid="faq-question" placeholder="Question" value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} className={`${inp} mt-3`}/>
      <textarea data-testid="faq-answer" placeholder="Answer" value={f.answer} onChange={(e) => setF({ ...f, answer: e.target.value })} className={`${inp} min-h-[100px] mt-3`}/>
      <div className="mt-4 flex gap-2 justify-end">
        <button onClick={onCancel} className="h-10 px-4 rounded-full border border-[#E5E7EB] bg-white text-sm">Cancel</button>
        <button data-testid="faq-save" onClick={() => { if (!f.question || !f.parent_slug) { toast.error("question and parent_slug required"); return; } onSave(f); }}
          className="btn-primary h-10 px-4 text-sm inline-flex items-center gap-2"><Save className="w-4 h-4"/> Save</button>
      </div>
    </div>
  );
}

function FAQRow({ faq, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState(faq);
  if (editing) return <FAQForm faq={f} onCancel={() => { setEditing(false); setF(faq); }} onSave={(u) => { onSave(u); setEditing(false); }}/>;
  return (
    <div data-testid={`faq-row-${faq.id}`} className="rounded-3xl border border-[#E5E7EB] bg-white p-5">
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{faq.parent_type} · {faq.parent_slug} · #{faq.order}</div>
          <div className="font-medium mt-1">{faq.question}</div>
          <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{faq.answer}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid={`faq-edit-${faq.id}`} onClick={() => setEditing(true)} className="text-sm text-blue-600 hover:underline">Edit</button>
          <button data-testid={`faq-del-${faq.id}`} onClick={onDelete} className="text-sm text-rose-600 hover:underline">Delete</button>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#2563EB]";
